import React, { useState } from "react";
import { Loader2, Lock, AlertCircle, ChevronRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { adminLogin, setAdminToken, setAdminUser, ADMIN_USERS, startAdminGoogleSignIn } from "@/lib/api";

const GoogleSignInButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-12 w-full items-center justify-center gap-3 rounded-md border text-sm font-semibold transition-colors hover:bg-slate-50"
    style={{ borderColor: "#CBD5E1", color: "#0B2545" }}
    data-testid="admin-google-signin"
  >
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
    Continue with Google
  </button>
);

export default function AdminLogin({ onLoggedIn }) {
  const [step, setStep] = useState("user"); // "user" | "password"
  const [userId, setUserId] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedUser = ADMIN_USERS.find((u) => u.id === userId);

  const chooseUser = (id) => {
    setUserId(id);
    setError("");
    setStep("password");
  };

  const backToUserSelect = () => {
    setStep("user");
    setPassword("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    if (!userId) {
      setStep("user");
      return;
    }
    try {
      setLoading(true);
      const data = await adminLogin(password, userId);
      setAdminToken(data.token);
      setAdminUser(userId);
      toast.success(`Welcome back, ${selectedUser?.name || ""}.`);
      onLoggedIn?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : status === 429
          ? "Too many failed attempts. Please wait a few minutes."
          : "Incorrect password.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-white px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo size="lg" />
        </div>
        <div className="tj-gold-rule mx-auto mt-7" />
        <h1
          className="font-display mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-center"
          style={{ color: "#0B2545" }}
        >
          Admin sign in
        </h1>
        <p className="mt-2 text-sm text-center" style={{ color: "#475569" }}>
          {step === "user"
            ? "Select your account to continue."
            : `Signing in as ${selectedUser?.name}. Enter the shared admin password or continue with Google.`}
        </p>

        {step === "user" ? (
          <div
            className="mt-8 rounded-xl border bg-white p-6 sm:p-8"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="admin-user-select"
          >
            <div
              className="mb-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium"
              style={{ color: "#0B2545" }}
            >
              <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
              Who&rsquo;s signing in?
            </div>
            <div className="flex flex-col gap-3">
              {ADMIN_USERS.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => chooseUser(u.id)}
                  className="group flex items-center justify-between gap-4 rounded-lg border p-4 text-left transition-all hover:-translate-y-[1px] hover:shadow-sm"
                  style={{ borderColor: "#E2E8F0", background: "#FFFFFF" }}
                  data-testid={`admin-user-${u.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-full font-display text-base font-bold shrink-0"
                      style={{ background: "rgba(212,175,55,0.16)", color: "#7A5E12" }}
                    >
                      {u.name[0]}
                    </span>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: "#0B2545" }}>
                        {u.name}
                      </div>
                      <div className="text-xs" style={{ color: "#475569" }}>
                        {u.role}
                      </div>
                    </div>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                    style={{ color: "#D4AF37" }}
                  />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 rounded-xl border bg-white p-6 sm:p-8"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="admin-login-form"
            noValidate
          >
            <button
              type="button"
              onClick={backToUserSelect}
              className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium"
              style={{ color: "#475569" }}
              data-testid="admin-back-to-user-select"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Choose a different user
            </button>

            <div
              className="mb-4 flex items-center gap-3 rounded-lg border p-3"
              style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
              data-testid="admin-selected-user"
            >
              <span
                className="grid place-items-center h-9 w-9 rounded-full font-display text-sm font-bold"
                style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
              >
                {selectedUser?.name?.[0]}
              </span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>
                  {selectedUser?.name}
                </div>
                <div className="text-[11px]" style={{ color: "#475569" }}>
                  {selectedUser?.role}
                </div>
              </div>
            </div>

            <Label
              htmlFor="admin-password"
              className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium"
              style={{ color: "#0B2545" }}
            >
              <Lock className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
              Shared admin password
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className="h-11"
              autoFocus
              autoComplete="current-password"
              data-testid="admin-password-input"
            />
            {error ? (
              <div
                className="mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
                data-testid="admin-login-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              className="mt-6 h-12 w-full text-base font-semibold tracking-wide text-white"
              style={{ background: "#0B2545" }}
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>

            <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em]" style={{ color: "#94A3B8" }}>
              <span className="h-px flex-1" style={{ background: "#E2E8F0" }} />
              or
              <span className="h-px flex-1" style={{ background: "#E2E8F0" }} />
            </div>
            <GoogleSignInButton onClick={() => startAdminGoogleSignIn(userId)} />
          </form>
        )}

        <p className="mt-6 text-center text-xs" style={{ color: "#475569" }}>
          Authorized personnel only · T&amp;J Clinical Solutions
        </p>
      </div>
    </div>
  );
}
